import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@heroui/react'
import { FOCUSABLE, trapStep } from '../lib/focusTrap'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Notice } from './settingsKit'

/**
 * The editor an administration row opens into: a centred dialog with a dark
 * header (ui/Dialog's classes) that asks "discard your changes?" once something
 * has been typed. Tab stays inside the top editor; focus returns on close.
 *
 * It is portalled to the body. A service's editor holds the delivered services
 * under it, each of which opens an editor of its own, and a <form> inside a <form>
 * is not HTML: the inner one's submit would reach the outer one's handler and save
 * the service somebody did not ask to save.
 *
 * Editors stack. Escape and Tab belong to the one on top.
 *
 * It says what the server refused inside the editor, where the person is looking.
 */

// The open editors, bottom to top.
const stack: number[] = []
let serial = 0

export function Sheet({
  title,
  mark,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  error,
  extra,
  closeLabel,
  hideSubmit = false,
  wide = false,
  asForm = true,
  submitClass = 'btn--primary',
  busyLabel,
  children,
}: {
  title: string
  /** Drawn before the title: a queue's stub, a desk's number. */
  mark?: ReactNode
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  /** What the server refused, said at the top of the editor. */
  error?: string
  /** Left side of the footer: a destructive action, or a short note. */
  extra?: ReactNode
  closeLabel?: string
  hideSubmit?: boolean
  wide?: boolean
  /** A <form>, so Enter in a field saves. Off for an editor holding controls this
   *  file did not write, where a <button> with no type would be a submit button. */
  asForm?: boolean
  /** The submit button's look. */
  submitClass?: string
  /** The submit button's words while busy; defaults to Saving. */
  busyLabel?: string
  children: ReactNode
}) {
  const { say } = useStaffText()
  const form = useRef<HTMLElement>(null)
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const id = useRef(0)
  const leave = () => (dirty.current ? setAsking(true) : onClose())
  const leaveRef = useRef(leave)
  leaveRef.current = leave

  useEffect(() => {
    id.current = ++serial
    stack.push(id.current)
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const field = form.current?.querySelector<HTMLElement>(
      '.dialog__body input:not([type=checkbox]):not([type=radio]), .dialog__body textarea',
    )
    ;(field ?? form.current)?.focus()
    const on = (e: KeyboardEvent) => {
      // A select's own list closes on Escape first, and only the top editor answers.
      if (e.defaultPrevented || stack[stack.length - 1] !== id.current) return
      if (e.key === 'Escape') {
        e.preventDefault()
        leaveRef.current()
      } else if (e.key === 'Tab' && form.current) {
        const items = [...form.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null)
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const to = trapStep(items, active, e.shiftKey)
        if (to) {
          e.preventDefault()
          to.focus()
        }
      }
    }
    addEventListener('keydown', on)
    return () => {
      removeEventListener('keydown', on)
      const at = stack.indexOf(id.current)
      if (at >= 0) stack.splice(at, 1)
      if (before?.isConnected) before.focus()
    }
  }, [])

  // A save that went through: whatever was typed is now what is stored.
  useEffect(() => {
    if (!busy && !error) dirty.current = false
  }, [busy, error])

  const submit = !hideSubmit && onSubmit && submitLabel
  const Frame = asForm ? 'form' : 'div'

  return createPortal(
    <div
      className="dialog-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) leave()
      }}
      // A submit stops at the editor's edge: React carries it up the component tree
      // through the portal, into whatever opened this one. Only React events are
      // stopped here; the window's Escape and Tab listeners still hear their keys.
      onSubmit={(e) => e.stopPropagation()}
      // Typing in an editor opened from this one is not an edit of this one's form.
      onInput={(e) => e.stopPropagation()}
      onChange={(e) => e.stopPropagation()}
    >
      <Frame
        ref={form as never}
        className={'dialog dialog--dark' + (wide ? ' dialog--wide' : '')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onSubmit={(e) => {
          e.preventDefault()
          if (valid && !busy && onSubmit) onSubmit()
        }}
        onInput={() => {
          dirty.current = true
        }}
        onChange={() => {
          dirty.current = true
        }}
      >
        <header className="dialog__head">
          {mark}
          <h2 className="dialog__title admin-sheet__title">{title}</h2>
          <button type="button" className="dialog__close" onClick={onClose} aria-label={say('ui.close')}>
            <Icon name="close" size={16} />
          </button>
        </header>
        <div className="dialog__body">
          {error && <Notice>{error}</Notice>}
          {children}
        </div>
        {asking ? (
          <footer className="dialog__foot admin-sheet__ask" role="alertdialog" aria-label={say('ui.unsaved')}>
            <div className="drawer__note">
              <strong>{say('ui.discardAsk')}</strong>
            </div>
            <Button variant="ghost" autoFocus onPress={() => setAsking(false)}>
              {say('ui.keepEditing')}
            </Button>
            <Button variant="danger" onPress={onClose}>
              {say('ui.discard')}
            </Button>
          </footer>
        ) : (
          <footer className="dialog__foot">
            <div className="drawer__note">{extra}</div>
            <Button variant="ghost" onPress={onClose}>
              {closeLabel ?? (submit ? say('common.cancel') : say('ui.close'))}
            </Button>
            {submit && (
              <Button
                type={asForm ? 'submit' : 'button'}
                variant={submitClass.includes('danger') ? 'danger' : submitClass.includes('primary') ? 'primary' : 'secondary'}
                isDisabled={!valid || busy}
                onPress={asForm ? undefined : () => valid && !busy && onSubmit?.()}
              >
                {busy ? (busyLabel ?? say('common.saving')) : submitLabel}
              </Button>
            )}
          </footer>
        )}
      </Frame>
    </div>,
    document.body,
  )
}
